import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Part UPC import — part id resolution and required UPC (trim-aware; empty CSV cells are "").
 * Follows the multi-phase contract on {@link BaseImportTransformer}; phase 4 is not gated on
 * `partId` so INVALID_PART and MISSING_REQUIRED_FIELDS can appear on the same row.
 * Preview (`!isFinalRun`): placeholder rows where needed + merged `preventativeIssues`.
 * Final (`isFinalRun`): `apiRequests[i]` is `null` when the row has a preventative issue.
 */
export class PartUpcImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 2: batched part lookup ────────────────────────────────────────
        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        // ── Phase 3: unknown part ───────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 4: required UPC (trim length; not gated on part resolution) ─────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.item.upc?.trim()?.length) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Emit merged issues ───────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, upc: undefined};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, upc: undefined};
            }

            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);

            const upcList = item.upc?.split(",").map((u) => u.trim()).filter(Boolean) ?? undefined;

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {partId: partId ?? null, partNumber: item.partNumber, upc: item.upc, upcList: upcList};
            }

            return {
                partId,
                upc: item.upc?.trim() ?? undefined,
                upcList: upcList,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
